import { useEffect, useState } from "react";
import { fileUploadService } from "../../Api/Services/fileUploadService";
import { SimplifiedUploadedFile } from "../../Models/UploadedFile";
import { FaFilePdf, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";

type FileUploaderProps = {
  label: string;
  file?: SimplifiedUploadedFile | null;
  context: string;
  purpose: string;
  accept?: string;
  referenceId?: string;
  onUploadComplete: (file: SimplifiedUploadedFile) => void;
  onDeleteComplete: () => void;
  deferUpload?: boolean;
  pendingFile?: File | null;
  onPendingFileChange?: (file: File | null) => void;
};

const FileUploader = ({
  label,
  file,
  context,
  purpose,
  accept,
  referenceId,
  onUploadComplete,
  onDeleteComplete,
  deferUpload = false,
  pendingFile = null,
  onPendingFileChange
}: FileUploaderProps) => {
  const [loading, setLoading] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);

  useEffect(() => {
    if (pendingFile) {
      const url = URL.createObjectURL(pendingFile);
      setLocalPreview(url);
      return () => URL.revokeObjectURL(url);
    }
    setLocalPreview(null);
  }, [pendingFile]);

  const resolvedUrl = pendingFile ? localPreview : file?.url ?? null;
  const resolvedName = pendingFile?.name ?? file?.blobName ?? "";
  const isImage = pendingFile
    ? pendingFile.type.startsWith("image/") || pendingFile.name.match(/\.(jpeg|jpg|gif|png|webp)$/i)
    : resolvedUrl?.match(/\.(jpeg|jpg|gif|png|webp)$/i);
  const isPdf = pendingFile
    ? pendingFile.type === "application/pdf" || pendingFile.name.match(/\.pdf$/i)
    : resolvedUrl?.match(/\.pdf$/i);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (deferUpload) {
        onPendingFileChange?.(file);
        toast.success("Arquivo adicionado. Ele será enviado ao salvar.");
        return;
      }

      try {
        setLoading(true);
        const uploadedFile = await fileUploadService.uploadFile({ 
          referenceId: referenceId ?? null, 
          context, 
          file,
          purpose
        });
        onUploadComplete(uploadedFile);
        toast.success("Arquivo salvo com sucesso!");
      } catch (err) {
        toast.error("Erro ao salvar arquivo, tente novamente mais tarde.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async () => {
    if (!file) {
      if (deferUpload && pendingFile) {
        onPendingFileChange?.(null);
        toast.success("Arquivo removido.");
      }
      return;
    }

    try {
      setLoading(true);
      const deleted = await fileUploadService.deleteFile({ id: file.id, blobName: file.blobName });
      if (deleted) {
        onDeleteComplete();
        toast.success("Arquivo deletado com sucesso!");
      }
    } catch (err) {
      toast.error("Erro ao deletar o arquivo, tente novamente mais tarde.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center">
        <label className="text-gray-600">{label}</label>

        {(file?.url || pendingFile) && (
          <button
            onClick={handleDelete}
            className="ml-2 text-gray-600 hover:text-gray-700"
            disabled={loading}
          >
            <FaTrash />
          </button>
        )}
      </div>

      <input 
        type="file" 
        onChange={handleFileChange}
        accept={accept}
        className="hidden"
        id={`file-input-${label}`}
      />

      <label htmlFor={`file-input-${label}`} className="cursor-pointer">
        {resolvedUrl ? (
          <>
            {isImage ? (
              <img
                src={resolvedUrl}
                alt="Preview"
                className="w-32 h-32 object-contain mt-2"
              />
            ) : isPdf ? (
              <a
                href={resolvedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-32 h-32 bg-gray-200 text-sm mt-2 flex flex-col items-center justify-center"
              >
                <FaFilePdf size={18} />
                <span>{"Visualizar PDF"}</span>
              </a>
            ) : (
              <div className="mt-2 text-gray-700">
                <span>{resolvedName}</span>
              </div>
            )}
          </>
        ) : (
          <div className="w-32 h-32 bg-gray-200 text-sm mt-2 flex items-center text-center">
            {"Nenhum arquivo selecionado"}
          </div>
        )}
      </label>
    </div>
  );
};

export default FileUploader;
